import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Breadcrum, Button, Grid, Flex, Text, Card } from "atoms";
import {
  BasicInfoForm,
  CompliancesAndApprovalsForm,
  ConnectivityForm,
  DesignAnalysis,
  LegalForm,
  NeighbourhoodForm,
  PricingForm,
  SocialInfrastructureForm,
  SpecificationAndAmenities,
} from "molecules";
import { Stepper as MyStepper } from "organisms/Stepper/Stepper";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePropsoch } from "contexts/Propsoch";
import { PLOT, PROPERTY_STATUS, VILLA } from "utils/constants";

const AddProperty = () => {
  const [searchParams] = useSearchParams();
  const step = parseInt(searchParams.get("step") || 0) || 0;
  const { id } = useParams();
  const navigate = useNavigate();

  const {
    actions: { getPropertyDetailsById },
  } = usePropsoch();

  // fetch property
  const [property, setProperty] = useState(null);

  const fetchProperty = useCallback(async () => {
    if (id) {
      const data = await getPropertyDetailsById(id);
      setProperty(data);
    }
  }, [id, getPropertyDetailsById]);

  useEffect(() => {
    fetchProperty();
  }, [fetchProperty]);

  const steps = useMemo(() => {
    const stepperSteps = [
      {
        name: "Basic Info",
        status: property?.[PROPERTY_STATUS.basicInfo],
      },
      {
        name: "Neighbourhood",
        status: property?.[PROPERTY_STATUS.neighbourhood],
      },
      {
        name: "Social Infrastructure",
        status: property?.[PROPERTY_STATUS.socialInfra],
      },
      {
        name: "Connectivity",
        status: property?.[PROPERTY_STATUS.connectivity],
      },
      {
        name: "Design Analysis",
        status:
          property?.[PROPERTY_STATUS.masterPlan] &&
          property?.[PROPERTY_STATUS.siteChallanges] &&
          (property?.types?.[0]?.name?.toLowerCase()?.includes(PLOT) ||
          property?.types?.[0]?.name?.toLowerCase()?.includes(VILLA)
            ? true
            : property?.[PROPERTY_STATUS.towerUnitPlan]),
      },
      {
        name: "Specification & Amenities",
        status:
          property?.[PROPERTY_STATUS.specifications] &&
          property?.[PROPERTY_STATUS.amenities],
      },
      { name: "Pricing", status: property?.[PROPERTY_STATUS.pricing] },
      {
        name: "Compliances & Approvals",
        status: property?.[PROPERTY_STATUS.compliance],
      },
      { name: "Legal", status: property?.[PROPERTY_STATUS.legal] },
    ];

    // last filled step redirection

    if (
      (property && step && !stepperSteps?.[step - 1]?.status) ||
      isNaN(parseInt(searchParams.get("step")))
    ) {
      const lastStep =
        stepperSteps?.findIndex((i) => !i?.status) === -1
          ? stepperSteps?.length - 1
          : stepperSteps?.findIndex((i) => !i?.status);
      navigate({
        pathname: `/property-listing/add/${id}`,
        search: `?${new URLSearchParams({
          step: lastStep,
        }).toString()}`,
      });
    }
    return stepperSteps;
  }, [property, step, id, navigate, searchParams]);

  const stepsComponents = useMemo(() => {
    return [
      { Component: BasicInfoForm },
      { Component: NeighbourhoodForm },
      { Component: SocialInfrastructureForm },
      { Component: ConnectivityForm },
      { Component: DesignAnalysis },
      { Component: SpecificationAndAmenities },
      { Component: PricingForm },
      { Component: CompliancesAndApprovalsForm },
      { Component: LegalForm },
    ];
  }, []);

  //
  // const [hitOnSave, setHitOnSave] = useState(false);
  const hitOnSave = useRef();
  const bindHitOnSave = useCallback((cb) => {
    hitOnSave.current = cb;
  }, []);

  // step any handler
  const onJumpToStep = useCallback(
    (step, modifiedID) => {
      step >= 0 &&
        step < steps?.length &&
        (steps?.[step]?.status || steps?.[step - 1]?.status) &&
        navigate({
          pathname: `/property-listing/add/${modifiedID || id}`,
          search: `?${new URLSearchParams({
            step: step,
          }).toString()}`,
        });
    },
    [id, navigate, steps]
  );
  // step next handler
  const onNext = useCallback(
    (modifiedID) => {
      step + 1 < steps?.length
        ? navigate({
            pathname: `/property-listing/add/${modifiedID || id}`,
            search: `?${new URLSearchParams({
              step: (step || 0) + 1,
            }).toString()}`,
          })
        : navigate("/property-listing");
    },
    [step, id, navigate, steps]
  );
  // step previous handler
  const onPrevious = useCallback(() => {
    navigate({
      pathname: `/property-listing/add/${id}`,
      search: `?${new URLSearchParams({
        step: step - 1 && step - 1 > 0 ? step - 1 : 0,
      }).toString()}`,
    });
  }, [step, id, navigate]);

  return (
    <Box>
      <Flex
        justifyContent="space-between"
        pb="2.4rem"
        position="sticky"
        top="0"
        bg="background.200"
        zIndex="2"
      >
        <Box>
          <Breadcrum
            firstText="Property Listing"
            firstUrl="/property-listing"
            secondText={property?.name || "New Project"}
            thirdText={steps?.[step]?.name}
          />
          <Text
            color="primary.900"
            fontSize="2.4rem"
            fontFamily="NeometricBold"
            mt="1.8rem"
          >
            {property?.name || "New Project"}
          </Text>
        </Box>
        <Button
          variant="secondary"
          width="fit-content"
          px="2rem"
          height="4.8rem"
          onClick={() => {
            hitOnSave.current();
            fetchProperty();
          }}
        >
          Save
          {/* {step === steps?.length - 1 ? "Save & Publish" : "Save"} */}
        </Button>
      </Flex>
      <Grid gridTemplateColumns="2.5fr 1fr" gridGap="2.4rem" alignItems="start">
        <Card>
          {[stepsComponents?.[step]]?.map(({ Component }, index) => (
            <Component
              key={index}
              onNext={onNext}
              onPrevious={onPrevious}
              bindHitOnSave={bindHitOnSave}
              fetchProperty={fetchProperty}
              rootProperty={property}
            />
          ))}
          {/* {
            {
              0: (
                <BasicInfoForm
                  onNext={onNext}
                  bindHitOnSave={bindHitOnSave}
                  fetchProperty={fetchProperty}
                />
              ),
              1: (
                <NeighbourhoodForm
                  onNext={onNext}
                  onPrevious={onPrevious}
                  bindHitOnSave={bindHitOnSave}
                  fetchProperty={fetchProperty}
                />
              ),
              2: (
                <SocialInfrastructureForm
                  onNext={onNext}
                  onPrevious={onPrevious}
                  bindHitOnSave={bindHitOnSave}
                  fetchProperty={fetchProperty}
                />
              ),
              3: (
                <ConnectivityForm
                  onNext={onNext}
                  onPrevious={onPrevious}
                  bindHitOnSave={bindHitOnSave}
                  fetchProperty={fetchProperty}
                />
              ),
              4: (
                <DesignAnalysis
                  onNext={onNext}
                  onPrevious={onPrevious}
                  bindHitOnSave={bindHitOnSave}
                  fetchProperty={fetchProperty}
                />
              ),
              5: (
                <SpecificationAndAmenities
                  onNext={onNext}
                  onPrevious={onPrevious}
                  bindHitOnSave={bindHitOnSave}
                  fetchProperty={fetchProperty}
                />
              ),
              6: (
                <PricingForm
                  onNext={onNext}
                  onPrevious={onPrevious}
                  bindHitOnSave={bindHitOnSave}
                  fetchProperty={fetchProperty}
                />
              ),
              7: (
                <CompliancesAndApprovalsForm
                  onNext={onNext}
                  onPrevious={onPrevious}
                  bindHitOnSave={bindHitOnSave}
                  fetchProperty={fetchProperty}
                />
              ),
              8: (
                <LegalForm
                  onNext={onNext}
                  onPrevious={onPrevious}
                  bindHitOnSave={bindHitOnSave}
                  fetchProperty={fetchProperty}
                />
              ),
            }[step]
          } */}
        </Card>

        <Card position="sticky" top="9rem">
          <MyStepper
            activeStep={step}
            steps={steps}
            stepNavigate={onJumpToStep}
          />
        </Card>
      </Grid>
    </Box>
  );
};

export default AddProperty;
